
import React, { useState } from 'react';
import FadeInSection from './FadeInSection';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(2, { message: 'กรุณาระบุชื่อของคุณ' }),
  company: z.string().min(2, { message: 'กรุณาระบุชื่อบริษัท' }),
  phone: z.string().min(9, { message: 'กรุณาระบุเบอร์โทรศัพท์ที่ถูกต้อง' }),
  email: z.string().email({ message: 'กรุณาระบุอีเมลที่ถูกต้อง' }),
  details: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const PricingSection: React.FC = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      company: '',
      phone: '',
      email: '',
      details: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      // DDOS Protection: Check for rapid submissions
      const now = Date.now();
      const timeSinceLastSubmit = now - lastSubmitTime;
      
      // Prevent submissions if less than 3 seconds have passed since last submission
      if (timeSinceLastSubmit < 3000) {
        toast({
          title: "กรุณารอสักครู่",
          description: "มีการส่งข้อมูลบ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่",
          variant: "destructive",
        });
        return;
      }

      // Set submitting state to prevent multiple clicks
      if (isSubmitting) return;
      setIsSubmitting(true);
      
      // Save form data to Supabase
      const { error } = await supabase
        .from('quote_requests')
        .insert([{
          name: data.name,
          company: data.company,
          phone: data.phone,
          email: data.email,
          details: data.details || '',
        }]);

      // Update last submit time
      setLastSubmitTime(now);

      if (error) {
        console.error('Error saving quote request:', error);
        
        // Show error toast
        toast({
          title: "เกิดข้อผิดพลาด",
          description: "ไม่สามารถส่งข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
          variant: "destructive",
        });
        return;
      }
      
      // Show success toast
      toast({
        title: "ส่งข้อมูลสำเร็จ",
        description: "ทีมงานจะติดต่อกลับโดยเร็วที่สุด ขอบคุณที่สนใจบริการของเรา",
        variant: "default",
      });
      
      // Reset form
      form.reset();
    } catch (err) {
      console.error('Exception when saving quote request:', err);
      
      // Show error toast
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถส่งข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
        variant: "destructive",
      });
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }
  };

  return (
    <section id="pricing" className="section-padding relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-carbon-green/5 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-carbon-blue/5 rounded-full blur-3xl -z-10"></div>
      
      <div className="container mx-auto">
        <FadeInSection>
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            <span className="text-carbon-green">อัตรา</span>ค่าบริการ
          </h2>
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
            ค่าบริการแพลตฟอร์มเริ่มต้นเพียง 1,500 บาทต่อผู้ใช้ต่อเดือน<br />
            กรอกแบบฟอร์มด้านล่างเพื่อขอใบเสนอราคาที่เหมาะกับความต้องการขององค์กรคุณ
          </p>
        </FadeInSection>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <FadeInSection delay={100}>
            <Card className="glass-card overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-carbon-green to-carbon-blue p-6 text-white">
                <CardTitle className="text-2xl md:text-3xl font-bold">
                  แพลตฟอร์มบริหารจัดการคาร์บอน
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-bold text-carbon-green">1,500</span>
                  <span className="text-lg text-gray-600 ml-2">บาท / ผู้ใช้ / เดือน</span>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>ระบบคำนวณคาร์บอนฟุตพรินต์ครบทั้ง Scope 1-3</p>
                  </div>
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>แดชบอร์ดติดตามผลแบบเรียลไทม์</p>
                  </div>
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>ระบบจัดการข้อมูลและรายงานอัตโนมัติ</p>
                  </div>
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>ที่ปรึกษาดูแลระบบส่วนตัว</p>
                  </div>
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>อัปเดตค่า Emission Factor อัตโนมัติ</p>
                  </div>
                  <div className="flex items-start">
                    <Check size={20} className="text-carbon-green mt-1 mr-2" />
                    <p>รองรับการทำงานร่วมกันของทีมงานหลายคน</p>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500 mb-4">
                  *ราคาไม่รวมภาษีมูลค่าเพิ่ม<br />
                  *ส่วนลดพิเศษสำหรับการสมัครแบบรายปี
                </div>
                
                {/* Removed the button from here */}
              </CardContent>
            </Card>
          </FadeInSection>
          
          <FadeInSection delay={200}>
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold mb-6">รับใบเสนอราคา</h3>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ชื่อ-นามสกุล</FormLabel>
                        <FormControl>
                          <Input placeholder="ชื่อของคุณ" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>ชื่อบริษัท</FormLabel>
                        <FormControl>
                          <Input placeholder="ชื่อบริษัทของคุณ" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>เบอร์โทรศัพท์</FormLabel>
                          <FormControl>
                            <Input placeholder="เบอร์โทรศัพท์" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>อีเมล</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="อีเมลของคุณ" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <FormField
                    control={form.control}
                    name="details"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>รายละเอียดเพิ่มเติม</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="โปรดระบุรายละเอียดความต้องการของคุณเพิ่มเติม" 
                            className="min-h-[120px]"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'กำลังส่ง...' : 'ส่งคำขอใบเสนอราคา'}
                  </Button>
                </form>
              </Form>
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
